import { useEffect, useMemo, useState, type FC } from 'react';
import {
  UiDrawer,
  UiDrawerOverlay,
  UiDrawerContent,
  UiDrawerBody,
  UiDrawerCloseButton,
  type UiDrawerProps,
  UiStack,
  UiText,
} from '@/lib/ui';
import BaseMessageBarError from '@/base/MessageBar/Error';
import PayByStripeWrapper from './PayByStripeWrapper';
import Cart from '@/registration/component/Register/ShoppingCart/Cart';
import { useCartQuery } from '@/registration/hook/useCartQuery';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import PayByEWayWrapper from './PayByEWayWrapper';
import PayBySecurePayWrapper from './PayBySecurePayWrapper';
import PayByNabTransactWrapper from './PayByNabTransactWrapper';

export type CreditCardGateway = 'stripe' | 'eWay' | 'secure_pay' | 'nab_transact';

export interface PayByCardDrawerProps extends Omit<UiDrawerProps, 'children'> {
  creditCardGateway: CreditCardGateway
}

const PayByCardDrawer: FC<PayByCardDrawerProps> = ({
  ...props
}) => {
  const [errors, setErrors] = useState<string[]>([]);
  const { eventId } = useRegisterRoute();
  const { data: cart } = useCartQuery(eventId);
  const cartItemErrors = useMemo(() => {
    let cartErrors: string[] = [];
    cart?.cartItems.forEach((cartItem) => {
      cartErrors = cartErrors.concat(cartItem.errors ?? []);
    });
    return cartErrors;
  }, [cart?.cartItems]);

  useEffect(() => {
    if (!props.isOpen && errors.length > 0) {
      setErrors([]);
    }
  }, [errors.length, props.isOpen]);

  return (
    <UiDrawer placement={'right'} size={'xl'} {...props}>
      <UiDrawerOverlay />
      <UiDrawerContent bgColor={'gray.100'}>
        <UiDrawerCloseButton size={'lg'} fontWeight={'bold'} color={'primary.500'} />
        <UiDrawerBody px={0} pb={16} alignItems={'stretch'}>
          <UiStack
            px={{ base: 8, lg: 8 }}
            py={8}
            flexGrow={1}
            spacing={6}
          >
            <UiText variant={'h6'}>Pay by card</UiText>
          </UiStack>
          <UiStack px={{ base: 8, lg: 8 }} flexGrow={1} alignItems={'stretch'}>
            <Cart showErrors={true} />
            { props.creditCardGateway === 'stripe' && (
              <PayByStripeWrapper
                isValid={errors.length === 0 && cartItemErrors.length === 0}
                setErrors={setErrors}
              />)
            }
            { props.creditCardGateway === 'eWay' && (
              <PayByEWayWrapper
                isValid={errors.length === 0 && cartItemErrors.length === 0}
                setErrors={setErrors}
              />)
            }
            { props.creditCardGateway === 'secure_pay' && (
              <PayBySecurePayWrapper
                isValid={errors.length === 0 && cartItemErrors.length === 0}
                setErrors={setErrors}
              />)
            }
            { props.creditCardGateway === 'nab_transact' && (
              <PayByNabTransactWrapper
                isValid={errors.length === 0 && cartItemErrors.length === 0}
                setErrors={setErrors}
              />)
            }
          </UiStack>
          {(errors.length > 0 && cartItemErrors.length === 0) && (
            <UiStack spacing={4} flexGrow={1} py={4} px={8}>
              {errors.map((error) => {
                return (
                  <BaseMessageBarError key={error} mt={3}>{error}</BaseMessageBarError>
                );
              })}
            </UiStack>
          )}
        </UiDrawerBody>
      </UiDrawerContent>
    </UiDrawer>
  );
};

export default PayByCardDrawer;
